import React, {
  memo,
  useState,
  Fragment,
  useEffect,
  useContext,
  useRef,
} from 'react';
import moment from 'moment-timezone';
import { toPng } from 'html-to-image';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import {
  ActionIcon,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  SectionHeader,
  ReactPDFWrapper,
} from '@mummssoftware/common-ui';
import styled from 'styled-components';
import {
  SET_PDF_CERT_APP_SNAPSHOT,
  MummsPDFGlobalContext,
  INCLUDE_PDF_CERT_APP,
} from '@mummssoftware/mumms-print';
import { CertGlobalContext } from '@mummssoftware/global-context';
import { HBCTreeCollapse, HBCTreeExpand } from '@mummssoftware/icons';
import { Card, STRING_TRUE } from '@mummssoftware/utils/constants';
import { CertContext } from '../../Context';
import CertForm from './CertForm';
import PatientCertHeader from '../Patient/PatientHeader';
import {
  filterByBenefitStartDateFunc,
  convertPatientCertHistoryToCertForms,
} from '../../utils';

const PageBreak = styled.div`
  @media print {
    visibility: hidden;
  }
`;

const PrintDiv = styled.div`
  @media print {
    page-break-after: always;
  }
`;

/**
 * @typedef Props
 * @param {*} param0
 */
/**
 * @typedef CertHistoryProps
 * @prop {import('./CertForm').CertFormProps} certProps Props for the CertForm component
 * @prop {function} formatMessage react-intl i18n function
 * @prop {function} getCertKeys
 * @prop {*[]} forms
 * @prop {function} onHide
 * @prop {import('../SharedPropDefs').Patient} patient
 * @prop {string} [patientNumber]
 * @prop {number} [payerId]
 */

/**
 *
 * @param {CertHistoryProps} props
 */
const CertHistory = ({
  certProps,
  formatMessage,
  getCertKeys,
  onHide,
  forms,
  patientNumber,
  patient,
  payerId,
  printConfig,
  mostRecentCert,
  self,
  headerProps,
}) => {
  // state and logic for getting all certifications for all carriers
  const { patientCertHistory } = useContext(CertGlobalContext);
  const { fromDate, toDate } = printConfig;
  const { pim } = useContext(CertContext);
  const [allFormsState, setAllFormsState] = useState([]);

  const [allCertFormsLoaded, setAllCertFormsLoaded] = useState(false);
  const globalCertHistory =
    Array.isArray(patientCertHistory) && patientCertHistory?.length > 0
      ? patientCertHistory[0].certHistory
      : [];
  const pdfImageRef = useRef([]);

  // logic for populating allFormsState to be used in the reactPDF workflow.
  useEffect(() => {
    const getGenerateForms = async () => {
      let certFormList = await convertPatientCertHistoryToCertForms(
        globalCertHistory,
        pim,
      );
      if (fromDate && toDate) {
        certFormList =
          Array.isArray(certFormList) &&
          certFormList.filter((cert) =>
            filterByBenefitStartDateFunc(
              moment(fromDate),
              moment(toDate),
              moment(cert?.startDate),
              moment(cert?.endDate),
            ),
          );
      }

      const filteredCertForms = certFormList.filter(
        ({ complete, verbalComplete }) => !!complete || !!verbalComplete,
      );
      setAllFormsState(filteredCertForms);
      setAllCertFormsLoaded(true);
    };
    if (!allCertFormsLoaded) {
      getGenerateForms();
    }
  }, []);

  // general state and helper functions.
  const [expanded, setExpanded] = useState(true);
  const [childrenExpanded, setChildrenExpanded] = useState(true);
  const currentHistory = patient.certHistory[payerId];
  const handleExpand = (e) => {
    if (e.target.closest('#child-expand')) {
      if (!expanded && !childrenExpanded) {
        setExpanded(true);
      }
      setChildrenExpanded(!childrenExpanded);
    } else {
      setExpanded(!expanded);
    }
  };

  const {
    dispatchReactPDF,
    includePdfCertApp,
    pdfCertAppSnapshot,
  } = useContext(MummsPDFGlobalContext);

  /*
  easiest way to think of pdfCertAppSnapshot is 2 modes:

    1. type AllCertificationsPDFData (used while collecting hydrated certs):
        - certsPdfData: array of objects of type SingleCertPDFData that contain the hydrated ref,
          and identifiers for each form
        - allPayersFormCount: the total count of ALL forms across ALL payers
        - payers: array of payerIds / keeps track of all payers being processed until it matches with visibleSectionsLength
        visibleSectionsLength = amount of payers (sections)

    2. type CertificationsSnapshot (used when all certs are processed/ready for ReactPDF)
        - snapshot: an array of all image src for the certs turned into .pngs for ReactPDF to display

  */

  const ReactPDFView = includePdfCertApp === STRING_TRUE;

  useEffect(() => {
    /*
    this is where the logic is caught to turn the "react-pdf" view of cert-app
    into a PNG image and passed to ReactPDF

    1. catches hydrated form components "behind the scenes"
    2. turns them all into PNGs
    3. gives the PNGs to ReactPDF

    */

    if (
      ReactPDFView &&
      allFormsState?.length > 0 &&
      allFormsState?.length === pdfImageRef.current.length &&
      !pdfCertAppSnapshot
    ) {
      const pdfImageRefCopy = [...pdfImageRef.current];

      // removes display: 'none' from ReactPDFWrapper
      pdfImageRefCopy.forEach((certData) => {
        certData.style.display = '';
      });

      const heightWidthConfig =
        printConfig.view === Card
          ? {
              height: 1230,
              width: 850,
            }
          : { width: 800, height: 200 };

      const reactPDFCertImages = pdfImageRefCopy.map((cert, index) =>
        toPng(
          cert,
          // slightly shrinks the height of the png if it's the first "Card" cert image to allow room for "Certifications" title etc
          index === 0 && printConfig.view === Card
            ? { width: 850, height: 1230 }
            : heightWidthConfig,
        ).then((res) => res),
      );

      if (reactPDFCertImages.length > 0) {
        Promise.all(reactPDFCertImages).then((res) => {
          dispatchReactPDF({
            type: SET_PDF_CERT_APP_SNAPSHOT,
            payload: res,
          });
          dispatchReactPDF({
            type: INCLUDE_PDF_CERT_APP,
            payload: '',
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfImageRef.current.length]);

  const certsToShow = ReactPDFView ? allFormsState : forms;
  return printConfig && printConfig.view === Card ? (
    <ReactPDFWrapper
      id={ReactPDFView ? 'react-pdf-cert' : ''}
      reactPdfView={ReactPDFView}
    >
      {certsToShow?.length > 0 ? (
        certsToShow.map((form, i) => {
          const { length } = certsToShow;
          const currentCert = form.cert;
          const certKeys = getCertKeys(form.formType);
          const onHideBound = () => onHide(form.formType, form.certIndex);
          return (
            <div
              // eslint-disable-next-line no-return-assign
              ref={(ref) => {
                // makes hydrated component accessible even though display: 'none' (React DOM)
                if (ref && ReactPDFView) {
                  pdfImageRef.current[i] = ref;
                }
              }}
              // eslint-disable-next-line react/no-array-index-key
              key={`${form.certIndex}-${i}`}
            >
              <PrintDiv>
                {printConfig.view === Card && !ReactPDFView && (
                  <PatientCertHeader
                    key={`patient-header-${patientNumber}`}
                    patient={patient}
                    primaryAddress={patient.primaryAddress}
                    currentCert={mostRecentCert}
                    formatMessage={self.current.formatMessage}
                    {...headerProps}
                  />
                )}
                <CertForm
                  key={`cert-form-${patientNumber}-${payerId}-${currentCert?.electionNumber}-${currentCert?.episode?.number}-${form?.formType}`}
                  currentCert={currentCert}
                  currentCertKeys={certKeys}
                  expandable
                  expanded={childrenExpanded}
                  formPhysId={form.formPhysId}
                  formPhysRestricted={form.formPhysRestricted}
                  formType={form.formType}
                  printFormType={form.formType}
                  onHide={onHideBound}
                  historicalView
                  _patient={patient}
                  verbalOnly={!form.complete && form.verbalComplete}
                  {...certProps}
                  isDoctor={false}
                  printConfig={printConfig}
                />
              </PrintDiv>
              {printConfig && printConfig.view === Card && i !== length - 1 && (
                <PageBreak />
              )}
            </div>
          );
        })
      ) : (
        <div
          // eslint-disable-next-line no-return-assign
          ref={(ref) => {
            // need a way to trigger the react pdf work even if there are no forms to display.
            // but dont set the ref until the cert forms have all had a chance to load.
            if (ref && ReactPDFView && allCertFormsLoaded) {
              pdfImageRef.current = ref;
            }
          }}
          // eslint-disable-next-line react/no-array-index-key
          key="reactpdf-no-cert"
        ></div>
      )}
    </ReactPDFWrapper>
  ) : (
    <ReactPDFWrapper
      reactPdfView={ReactPDFView}
      id={ReactPDFView ? 'react-pdf-cert' : ''}
    >
      <ExpansionPanel expanded={expanded} onChange={handleExpand}>
        <ExpansionPanelSummary>
          <SectionHeader
            border
            action={
              <Fragment>
                <ActionIcon
                  id="child-expand"
                  title={
                    childrenExpanded
                      ? formatMessage({ id: 'CollapseChildren' })
                      : formatMessage({ id: 'ExpandChildren' })
                  }
                  Icon={childrenExpanded ? HBCTreeExpand : HBCTreeCollapse}
                />
                <ActionIcon
                  title={
                    expanded
                      ? formatMessage({ id: 'Collapse' })
                      : formatMessage({ id: 'Expand' })
                  }
                  Icon={expanded ? KeyboardArrowDown : KeyboardArrowRight}
                />
              </Fragment>
            }
            title={currentHistory[currentHistory.length - 1].carrierName}
          />
        </ExpansionPanelSummary>

        <ExpansionPanelDetails>
          {certsToShow?.length > 0 &&
            certsToShow.map((form, i) => {
              const currentCert = form.cert; // currentHistory[form.certIndex];
              const onHideBound = () => onHide(form.formType, form.certIndex);
              return (
                <div
                  // eslint-disable-next-line no-return-assign
                  ref={(ref) => {
                    // makes hydrated component accessible even though display: 'none' (React DOM)
                    if (ref && ReactPDFView) {
                      pdfImageRef.current[i] = ref;
                    }
                  }}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${form.certIndex}-${i}`}
                >
                  <CertForm
                    key={`cert-form-${patientNumber}-${payerId}-${currentCert?.electionNumber}-${currentCert?.episode?.number}-${form?.formType}`}
                    currentCert={currentCert}
                    currentCertKeys={getCertKeys(form.formType)}
                    expandable
                    expanded={childrenExpanded}
                    formPhysId={form.formPhysId}
                    formPhysRestricted={form.formPhysRestricted}
                    formType={form.formType}
                    printFormType={form.formType}
                    onHide={onHideBound}
                    historicalView
                    _patient={patient}
                    verbalOnly={!form.complete && form.verbalComplete}
                    {...certProps}
                    isDoctor={false}
                    printConfig={printConfig}
                  />
                </div>
              );
            })}
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </ReactPDFWrapper>
  );
};

export default memo(CertHistory);
